import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Formik } from "formik";
import { useQuery } from "@tanstack/react-query";
import {
  ErrorToast,
  SuccessToast,
  Confirmation,
} from "../../../../Utils/SweetAlert";
import {
  useMutateDeleteEmoji,
  useMutateDragDropEmoji,
} from "../../hooks/useApiHook";
import { GetEmojiList } from "../../../../Api/emoji";
import { EditButton, DeleteButton, AddButton } from "../../../../Components";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { IoEye } from "react-icons/io5";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import SearchBox from "../../../../Components/SearchBox";
import noImage from "../../../../assets/Images/no-image.png";
import "../../style.css";

const EmojiList = () => {
  const formRef = useRef();

  const deleteEmoji = useMutateDeleteEmoji();
  const dragdropEmoji = useMutateDragDropEmoji();

  const [emojiList, setEmojiList] = useState(null);

  const {
    isFetching: isEmojiLoading,
    data: emojiData,
    refetch,
  } = useQuery({
    queryKey: ["get-emoji-list"],
    queryFn: () => GetEmojiList(formRef?.current?.values?.search || ""),
    throwOnError: (error) => {
      ErrorToast(error?.response?.data?.message || "Server error.");
    },
  });

  useEffect(() => {
    if (emojiData) {
      setEmojiList(emojiData);
    }
  }, [emojiData]);

  const handleImageError = (e) => {
    e.currentTarget.src = noImage;
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const { source, destination } = result;

    const items = Array.from(emojiList.data);
    const [reorderedItem] = items.splice(source.index, 1);
    items.splice(destination.index, 0, reorderedItem);
    setEmojiList({ ...emojiList, data: items });

    dragdropEmoji.mutate(
      {
        emojiId: reorderedItem._id,
        newOrder: destination.index,
      },
      {
        onSuccess: () => {
          refetch();
        },
        onError: (error) => {
          ErrorToast(error?.response?.data?.message || "Failed to reorder.");
        },
      }
    );
  };

  return (
    <>
      <Helmet>
        <title>OVE | Admin Emoji</title>
      </Helmet>

      <div className="page-wrapper emoji-list">
        <div className="content container-fluid">
          <div className="admin-wrapper">
            <div className="page-header">
              <div className="col">
                <h1 className="page-title">Emoji</h1>
              </div>
              <div className="col-12 col-md-3 col-lg-2 px-2">
                <Formik initialValues={{ search: "" }} innerRef={formRef}>
                  {() => (
                    <form>
                      <SearchBox refetch={refetch} setPage={() => {}} />
                    </form>
                  )}
                </Formik>
              </div>
              <div>
                <AddButton to="/admin/add-emoji" permission="create-emoji" />
              </div>
            </div>

            {/* Table */}
            <div className="col-sm-12 table-width">
              <div className="cards-table card-table">
                <div className="card-body">
                  <div className="table-responsive emoji-table custom-pagination table-no-padding">
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <table
                        className="table datatable table-manager mb-0"
                        style={{ width: "100%" }}
                      >
                        <thead className="thead-light">
                          <tr>
                            <th className="col-icon text-start" style={{ width: "20%" }}>Image</th>
                            <th className="col-name text-start" style={{ width: "40%" }}>Name</th>
                            <th className="col-action text-center" style={{ width: "40%" }}>Action</th>
                          </tr>
                        </thead>

                        {isEmojiLoading && (
                          <tbody>
                            <tr>
                              <td align="center" colSpan={3}>
                                <div className="d-flex justify-content-center align-items-center py-4">
                                  <div className="spinner-border loader-theme" role="status" />
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        )}

                        {!isEmojiLoading && (
                          <Droppable droppableId="droppable">
                            {(provided) => (
                              <tbody
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                              >
                                {emojiList?.data?.map((item, index) => (
                                  <Draggable
                                    key={item._id}
                                    draggableId={item._id}
                                    index={index}
                                  >
                                    {(provided) => (
                                      <tr
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className="table disable-row"
                                      >
                                        <td className="col-icon text-start">
                                          <div className="drag-content">
                                            <img
                                              src={item.image_url}
                                              alt="emoji"
                                              className="list-image-news"
                                              style={{ boxShadow: "none" }}
                                              onError={handleImageError}
                                            />
                                          </div>
                                        </td>
                                        <td className="col-name text-start">
                                          <Tooltip title={item.name} placement="top">
                                            <span className="guide-cell-ellipsis">
                                              {item?.name || "-"}
                                            </span>
                                          </Tooltip>
                                        </td>
                                        <td className="col-action text-center">
                                          <EditButton
                                            to="/admin/edit-emoji"
                                            state={{ id: item?._id }}
                                            permission="edit-emoji"
                                          />
                                          <DeleteButton
                                            onDelete={() => {
                                              Confirmation("Are you sure you want to delete emoji?").then((res) => {
                                                if (res.isConfirmed) {
                                                  deleteEmoji.mutate(item?._id, {
                                                    onSuccess: (response) => {
                                                      SuccessToast(response?.message || "Deleted successfully.");
                                                      refetch();
                                                    },
                                                    onError: (error) => {
                                                      ErrorToast(error?.response?.data?.message || "Server error.");
                                                    },
                                                  });
                                                }
                                              });
                                            }}
                                            permission="delete-emoji"
                                          />
                                        </td>
                                      </tr>
                                    )}
                                  </Draggable>
                                ))}

                                {emojiList?.data?.length === 0 && (
                                  <tr>
                                    <td className="text-center" colSpan={3}>
                                      No Records Found.
                                    </td>
                                  </tr>
                                )}
                                {provided.placeholder}
                              </tbody>
                            )}
                          </Droppable>
                        )}
                      </table>
                    </DragDropContext>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmojiList;
